/**
 * Tools Service
 * Handles all tool-related API operations
 */

import { apiClient } from './api'

export interface ToolType {
  id: string
  name: string
  manufacturer?: string
  model?: string
  description?: string
  created_at: string
  updated_at: string
}

export interface Tool {
  id: string
  serial_number: string
  internal_id?: string
  sap_id?: string
  tool_type_id: string
  tool_type?: ToolType
  description?: string
  status: 'AVLB' | 'NUSE' | 'INSP' | 'MTCE' | 'REPR' | 'QRTD' | 'RTRD'
  location?: string
  total_runs: number
  total_hours: number
  total_feet: number
  runs_between_minor_maintenance?: number
  runs_between_major_maintenance?: number
  hours_between_maintenance?: number
  created_at: string
  updated_at: string
}

export interface ToolListResponse {
  total: number
  page: number
  page_size: number
  items: Tool[]
}

export interface ToolTypeListResponse {
  total: number
  page: number
  page_size: number
  items: ToolType[]
}

export interface ToolCreateInput {
  serial_number: string
  tool_type_id: string
  location?: string
  status?: string
  notes?: string
  internal_id?: string
  sap_id?: string
}

export interface ToolUpdateInput {
  serial_number?: string
  tool_type_id?: string
  location?: string
  status?: string
  notes?: string
  internal_id?: string
  sap_id?: string
}

export interface ToolTypeCreateInput {
  name: string
  manufacturer?: string
  model?: string
  description?: string
}

export interface ToolTypeUpdateInput {
  name?: string
  manufacturer?: string
  model?: string
  description?: string
}

// Tool Type APIs
export const toolsService = {
  // Tool Types
  async listToolTypes(page: number = 0, limit: number = 50) {
    const skip = (page - 1) * limit
    const response = await apiClient.get<ToolTypeListResponse>(
      `/tools/types?skip=${skip}&limit=${limit}`
    )
    return response.data
  },

  async getToolType(toolTypeId: string) {
    const response = await apiClient.get<ToolType>(`/tools/types/${toolTypeId}`)
    return response.data
  },

  async createToolType(data: ToolTypeCreateInput) {
    const response = await apiClient.post<ToolType>('/tools/types', data)
    return response.data
  },

  async updateToolType(toolTypeId: string, data: ToolTypeUpdateInput) {
    const response = await apiClient.put<ToolType>(`/tools/types/${toolTypeId}`, data)
    return response.data
  },

  async deleteToolType(toolTypeId: string) {
    await apiClient.delete(`/tools/types/${toolTypeId}`)
  },

  // Tools
  async listTools(
    page: number = 0,
    limit: number = 50,
    statusFilter?: string,
    location?: string
  ) {
    const skip = (page - 1) * limit
    let url = `/tools?skip=${skip}&limit=${limit}`
    if (statusFilter) url += `&status_filter=${statusFilter}`
    if (location) url += `&location=${encodeURIComponent(location)}`

    const response = await apiClient.get<ToolListResponse>(url)
    return response.data
  },

  async getTool(toolId: string) {
    const response = await apiClient.get<Tool>(`/tools/${toolId}`)
    return response.data
  },

  async createTool(data: ToolCreateInput) {
    const response = await apiClient.post<Tool>('/tools', data)
    return response.data
  },

  async updateTool(toolId: string, data: ToolUpdateInput) {
    const response = await apiClient.put<Tool>(`/tools/${toolId}`, data)
    return response.data
  },

  async deleteTool(toolId: string) {
    await apiClient.delete(`/tools/${toolId}`)
  },
}

export default toolsService
